import React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { momentDtoToLocalStringByCulture, parseMomentByCulture } from "../../aplUtils/dateHelper";
import { Culture } from "../../types/culture";
import "./DateSelector3.css";

const DateSelector3Calendar = (props: {
    currentCalendarView: Date;
    calendarWrapperRef: React.RefObject<HTMLDivElement>;
    setCurrentCalendarView: (date: Date) => void;
    selectedDate: Date | null;
    onSetSelectedDate: (date: Date) => void;
    culture: Culture;
    calendarPositionLeft: number;
    small?: boolean;
    marginTop?: string;
    clearable?: boolean;
}) => {
    const [monthDropDownVisible, setMonthDropDownVisible] = useState<boolean>(false);
    const [yearDropDownVisible, setYearDropDownVisible] = useState<boolean>(false);
    const selectedDateSafe = typeof props.selectedDate === "string" ? new Date(props.selectedDate) : props.selectedDate;

    const getMonthName = (monthNumber: number, culture: Culture) => {
        const date = new Date();
        date.setMonth(monthNumber);
        return date.toLocaleString(culture, { month: "long" });
    };

    const getDayOfMonth = (year: number, month: number) => {
        let monthIndex = month - 1;
        let date = new Date(year, monthIndex, 1);
        let result = [];
        while (date.getMonth() === monthIndex) {
            result.push({ date: date.getDate(), dayOfWeek: date.getDay(), year: date.getFullYear(), month: date.getMonth() + 1 });
            date.setDate(date.getDate() + 1);
        }
        return result;
    };

    const groupByWeek = (dates: any) => {
        const groups = [];
        let group = [];

        for (let i = 0; i < dates.length; i++) {
            const date = dates[i];
            group.push(date);

            if (date.dayOfWeek === 0 || group.length === 7) {
                groups.push(group);
                group = [];
            }
        }

        if (group.length > 0) {
            groups.push(group);
        }

        return groups;
    };

    const getWeekDaysNamesShort = (culture: Culture) => {
        let mon = new Date("2023-05-01 00:00:00").toLocaleString(culture, { weekday: "short" }).replace(".", "");
        let tue = new Date("2023-05-02 00:00:00").toLocaleString(culture, { weekday: "short" }).replace(".", "");
        let wed = new Date("2023-05-03 00:00:00").toLocaleString(culture, { weekday: "short" }).replace(".", "");
        let thu = new Date("2023-05-04 00:00:00").toLocaleString(culture, { weekday: "short" }).replace(".", "");
        let fri = new Date("2023-05-05 00:00:00").toLocaleString(culture, { weekday: "short" }).replace(".", "");
        let sat = new Date("2023-05-06 00:00:00").toLocaleString(culture, { weekday: "short" }).replace(".", "");
        let sun = new Date("2023-05-07 00:00:00").toLocaleString(culture, { weekday: "short" }).replace(".", "");
        return { sunday: sun, monday: mon, tuesday: tue, wednesday: wed, thursday: thu, friday: fri, saturday: sat };
    };

    const getMonthNames = (culture: Culture) => {
        let jan = new Date("2023-01-01 00:00:00").toLocaleString(culture, { month: "long" });
        let feb = new Date("2023-02-01 00:00:00").toLocaleString(culture, { month: "long" });
        let mar = new Date("2023-03-01 00:00:00").toLocaleString(culture, { month: "long" });
        let apr = new Date("2023-04-01 00:00:00").toLocaleString(culture, { month: "long" });
        let may = new Date("2023-05-01 00:00:00").toLocaleString(culture, { month: "long" });
        let jun = new Date("2023-06-01 00:00:00").toLocaleString(culture, { month: "long" });
        let jul = new Date("2023-07-01 00:00:00").toLocaleString(culture, { month: "long" });
        let aug = new Date("2023-08-01 00:00:00").toLocaleString(culture, { month: "long" });
        let sep = new Date("2023-09-01 00:00:00").toLocaleString(culture, { month: "long" });
        let oct = new Date("2023-10-01 00:00:00").toLocaleString(culture, { month: "long" });
        let nov = new Date("2023-11-01 00:00:00").toLocaleString(culture, { month: "long" });
        let dec = new Date("2023-12-01 00:00:00").toLocaleString(culture, { month: "long" });
        let selected = getMonthName(props.currentCalendarView.getMonth(), props.culture);
        return {
            january: jan,
            february: feb,
            march: mar,
            april: apr,
            may: may,
            june: jun,
            july: jul,
            august: aug,
            september: sep,
            october: oct,
            november: nov,
            december: dec,
            selected: selected
        };
    };

    const isToday = (date1: Date, date2: Date | null) =>
        date1.getFullYear() === (date2 ?? new Date()).getFullYear() &&
        date1.getMonth() === (date2 ?? new Date()).getMonth() &&
        date1.getDate() === (date2 ?? new Date()).getDate();

    const monthViewRow = (weeksDays: any[]) => {
        let views = [];

        let sYear = (selectedDateSafe ?? new Date()).getFullYear();
        let sMonth = (selectedDateSafe ?? new Date()).getMonth() + 1;

        if (weeksDays.some(x => x.date < 8)) {
            for (let daysIndex = 0; daysIndex < 7 - weeksDays.length; daysIndex++) {
                views.push(
                    <td key={`${sYear}${sMonth}empty${daysIndex}`} className={` ui-datepicker-days-cell-over`}>
                        &nbsp;
                    </td>
                );
            }
        }

        for (let daysIndex = 0; daysIndex < weeksDays.length; daysIndex++) {
            let dateObject = new Date(
                weeksDays[daysIndex].year,
                weeksDays[daysIndex].month - 1,
                weeksDays[daysIndex].date,
                props.currentCalendarView.getHours(),
                props.currentCalendarView.getMinutes(),
                props.currentCalendarView.getSeconds()
            );
            let selected = isToday(dateObject, selectedDateSafe);
            var today = isToday(dateObject, new Date());
            views.push(
                <td
                    className={`${selected ? "xdsoft_current" : ""} ${
                        today ? "xdsoft_current_day" : ""
                    } xdsoft_date xdsoft_day_of_week1 xdsoft_date `}
                    onClick={(e: any) => {
                        e.stopPropagation();
                        props.onSetSelectedDate(dateObject);
                    }}
                    key={`${weeksDays[daysIndex].year}${weeksDays[daysIndex].month}${weeksDays[daysIndex].date}`}
                    id={`${dateObject.toISOString()}`}
                >
                    <div>{weeksDays[daysIndex].date}</div>
                </td>
            );
        }
        return views;
    };

    const monthView = (days: any) => {
        let itemsByWeek = groupByWeek(days);
        return itemsByWeek.map((x, y) => <tr key={`week-${x}-${y}`}>{monthViewRow(itemsByWeek[y])}</tr>);
    };

    const changeMonth = (monthOffset: number) => {
        var newDate = new Date(props.currentCalendarView.setMonth(props.currentCalendarView.getMonth() + monthOffset));
        props.setCurrentCalendarView(newDate);
        console.log(`${new Date().toISOString()} setCurrentCalendarView ${props.currentCalendarView.toISOString()}`);
    };

    const weekDays = getWeekDaysNamesShort(props.culture);
    const monthNames = getMonthNames(props.culture);

    const showMonthDropDown = () => {
        if (monthDropDownVisible) {
            setMonthDropDownVisible(false);
            setYearDropDownVisible(false);
        } else {
            setMonthDropDownVisible(true);
            setYearDropDownVisible(false);
        }
    };
    const showYearDropDown = () => {
        if (yearDropDownVisible) {
            setMonthDropDownVisible(false);
            setYearDropDownVisible(false);
        } else {
            setMonthDropDownVisible(false);
            setYearDropDownVisible(true);
        }
    };

    const yearDropDownRef = React.useRef<HTMLDivElement>(null);
    const monthDropDownRef = React.useRef<HTMLDivElement>(null);
    const timeDropDownRef = React.useRef<HTMLDivElement>(null);

    const generateYearDropDown = (date: Date) => {
        let currentYear = date.getFullYear();
        let startYear = currentYear - 10;
        let years = [];
        for (let i = startYear; i < startYear + 20; i++) {
            years.push(i);
        }

        return (
            <div
                className="xdsoft_scrollbar2"
                style={{ overflowY: "scroll", overflowX: "hidden", maxHeight: "10rem" }}
                ref={yearDropDownRef}
            >
                {years.map((x, y) => (
                    <div
                        key={x}
                        className={`xdsoft_option ${x === currentYear ? "xdsoft_current" : ""}`}
                        onClick={() => {
                            let newDate = new Date(props.currentCalendarView.setFullYear(x));
                            props.setCurrentCalendarView(newDate);
                        }}
                    >
                        {x}
                    </div>
                ))}
            </div>
        );
    };

    const generateMonthDropDown = (date: Date) => {
        let currentMonth = date.getMonth();
        let currentMonthName = getMonthName(currentMonth, props.culture);
        let months = [
            monthNames.january,
            monthNames.february,
            monthNames.march,
            monthNames.april,
            monthNames.may,
            monthNames.june,
            monthNames.july,
            monthNames.august,
            monthNames.september,
            monthNames.october,
            monthNames.november,
            monthNames.december
        ];
        for (let i = 0; i < months.length; i++) {}

        return (
            <div
                className="xdsoft_scrollbar2"
                style={{ overflowY: "scroll", overflowX: "hidden", maxHeight: "10rem" }}
                ref={monthDropDownRef}
            >
                {months.map((x, y) => (
                    <div
                        key={x}
                        className={`xdsoft_option ${x === currentMonthName ? "xdsoft_current" : ""}`}
                        onClick={() => {
                            let newDate = new Date(props.currentCalendarView.setMonth(y));
                            props.setCurrentCalendarView(newDate);
                        }}
                    >
                        {x}
                    </div>
                ))}
            </div>
        );
    };

    const hoursTo12Or24 = (hours: number, minutes: number, culture: Culture): string => {
        if (culture === "en-US") {
            if (hours > 12) {
                return `${(hours - 12).toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}pm`;
            } else if (hours === 0) {
                return `12:${minutes.toString().padStart(2, "0")}am`;
            } else {
                return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}am`;
            }
        } else {
            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        }
    };

    const generateTimeDropDown = (date: Date) => {
        const currentHour = date.getHours();
        const currentMinute = date.getMinutes();

        let times = [];
        for (let i = 0; i < 24; i++) {
            times.push(i);
        }

        let t = times.map((x, y) => (
            <React.Fragment key={x}>
                <div
                    className={`xdsoft_time ${x === currentHour && currentMinute < 30 ? "xdsoft_current" : ""}`}
                    onClick={e => {
                        e.stopPropagation();
                        let newDate = new Date(props.currentCalendarView.setHours(x));
                        let newDate2 = new Date(newDate.setMinutes(0));
                        props.onSetSelectedDate(newDate2);
                    }}
                >
                    {hoursTo12Or24(x, 0, props.culture)}
                </div>
                <div
                    className={`xdsoft_time ${x === currentHour && currentMinute >= 30 ? "xdsoft_current" : ""}`}
                    onClick={e => {
                        e.stopPropagation();
                        let newDate = new Date(props.currentCalendarView.setHours(x));
                        let newDate2 = new Date(newDate.setMinutes(30));
                        props.onSetSelectedDate(newDate2);
                    }}
                >
                    {hoursTo12Or24(x, 30, props.culture)}
                </div>
            </React.Fragment>
        ));

        return (
            <React.Fragment>
                {t}
                <div style={{ maxHeight: "0.6rem" }}></div>
            </React.Fragment>
        );
    };

    useEffect(() => {
        if (yearDropDownRef?.current && yearDropDownRef.current.scrollHeight)
            yearDropDownRef.current.scrollTop = yearDropDownRef?.current.scrollHeight / 2 - 33;
        if (monthDropDownRef?.current && monthDropDownRef.current.scrollHeight)
            monthDropDownRef.current.scrollTop = (monthDropDownRef?.current.scrollHeight / 12) * props.currentCalendarView.getMonth() - 33;

        if (timeDropDownRef?.current && timeDropDownRef.current.scrollHeight)
            timeDropDownRef.current.scrollTop =
                (timeDropDownRef?.current.scrollHeight / 1440) *
                    (props.currentCalendarView.getHours() * 60 + props.currentCalendarView.getMinutes()) -
                33;
    }, [yearDropDownVisible, monthDropDownVisible, props.currentCalendarView]);

    return (
        <React.Fragment>
            <div
                ref={props.calendarWrapperRef}
                className="xdsoft_datetimepicker xdsoft_noselect"
                style={{
                    display: "block",
                    position: "absolute",
                    marginTop: props.marginTop ?? props.small ? "1.5rem" : "0px",
                    marginLeft: props.calendarPositionLeft,
                    minWidth: "20rem"
                }}
            >
                <div className="xdsoft_datepicker active">
                    <div className="xdsoft_monthpicker">
                        <button
                            type="button"
                            className="xdsoft_prev"
                            style={{ visibility: "visible" }}
                            onClick={() => {
                                setMonthDropDownVisible(false);
                                setYearDropDownVisible(false);
                                changeMonth(-1);
                            }}
                        />
                        <button
                            type="button"
                            className="xdsoft_today_button"
                            style={{ visibility: "visible" }}
                            onClick={() => {
                                setMonthDropDownVisible(false);
                                setYearDropDownVisible(false);
                                props.setCurrentCalendarView(new Date());
                            }}
                        />
                        <div className="xdsoft_label xdsoft_month" onClick={showMonthDropDown}>
                            <span>{getMonthName(props.currentCalendarView.getMonth(), props.culture)}</span>
                            <div
                                className="xdsoft_select xdsoft_monthselect xdsoft_scroller_box"
                                style={{ display: monthDropDownVisible ? "block" : "none" }}
                            >
                                {generateMonthDropDown(props.currentCalendarView)}
                            </div>
                            <i />
                        </div>
                        <div className="xdsoft_label xdsoft_year" onClick={showYearDropDown}>
                            <span>{props.currentCalendarView.getFullYear()}</span>
                            <div
                                className="xdsoft_select xdsoft_yearselect xdsoft_scroller_box"
                                style={{ display: yearDropDownVisible ? "block" : "none" }}
                            >
                                {generateYearDropDown(props.currentCalendarView)}
                            </div>
                            <i />
                        </div>
                        <button
                            type="button"
                            className="xdsoft_next"
                            style={{ visibility: "visible" }}
                            onClick={() => {
                                setMonthDropDownVisible(false);
                                setYearDropDownVisible(false);
                                changeMonth(1);
                            }}
                        />
                    </div>
                    <div className="xdsoft_calendar">
                        <table>
                            <thead>
                                <tr>
                                    <th>{weekDays.monday}</th>
                                    <th>{weekDays.tuesday}</th>
                                    <th>{weekDays.wednesday}</th>
                                    <th>{weekDays.thursday}</th>
                                    <th>{weekDays.friday}</th>
                                    <th>{weekDays.saturday}</th>
                                    <th>{weekDays.sunday}</th>
                                </tr>
                            </thead>

                            <tbody>
                                {monthView(
                                    getDayOfMonth(props.currentCalendarView.getFullYear(), props.currentCalendarView.getMonth() + 1)
                                )}
                                {/* {returnOriginalTable()} */}
                            </tbody>
                        </table>
                    </div>
                    <button type="button" className="xdsoft_save_selected blue-gradient-button" style={{ display: "none" }}>
                        Save Selected
                    </button>
                </div>
                <div className="xdsoft_timepicker active">
                    <button type="button" className="xdsoft_prev" />
                    <div className="xdsoft_time_box xdsoft_scroller_box">
                        <div
                            className="xdsoft_time_variant xdsoft_scrollbar2"
                            style={{ overflowY: "scroll", overflowX: "hidden", maxHeight: "10rem" }}
                            ref={timeDropDownRef}
                        >
                            {generateTimeDropDown(props.currentCalendarView)}
                        </div>
                    </div>
                    <button type="button" className="xdsoft_next" />
                </div>
            </div>
        </React.Fragment>
    );
};

const DateSelector3 = (props: {
    name?: string;
    culture: Culture;
    onSetSelectedDate: (x: Date | null) => void;
    selectedDate: Date | null | undefined;
    onCalendarClose?: () => void;
    isCalendarOpen?: boolean | null;
    small?: boolean;
    marginTop?: string;
    readonly?: boolean;
    clearable?: boolean;
}): JSX.Element => {
    const onCalendarClose = props.onCalendarClose;
    const onSetSelectedDate = props.onSetSelectedDate;

    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
    const [calendarPositionLeft, setCalendarPositionLeft] = useState<number>(0);
    const [currentCalendarView, setCurrentCalendarView] = useState<Date>(new Date());
    const [textToParse, setTextToParse] = useState<string>("");
    const calendarWrapperRef = useRef<HTMLDivElement>(null);
    const inputFieldRef = useRef<HTMLInputElement>(null);
    const [invalidDateInput, setInvalidDateInput] = useState<boolean>(false);

    function openCalendar(e: any) {
        let left = inputFieldRef.current?.offsetLeft;
        if (left) setCalendarPositionLeft(left);
        setIsCalendarOpen(true);
    }

    const closeCalendar = useCallback(() => {
        setIsCalendarOpen(false);

        if (onCalendarClose) {
            onCalendarClose();
        }
    }, [onCalendarClose]);

    const onOutsideAction = useCallback(
        (event: Event) => {
            const { current: calendarWrapperRefCurrent } = calendarWrapperRef;
            const target = ("composedPath" in event ? event.composedPath()[0] : (event as Event).target) as HTMLElement;

            if (target && (!calendarWrapperRefCurrent || !calendarWrapperRefCurrent.contains(target))) {
                console.log(`${new Date().toISOString()} - onOutsideAction - closeCalendar()`);
                closeCalendar();
            } else {
                console.log(`${new Date().toISOString()} - onOutsideAction - do nothing`);
            }
        },
        [calendarWrapperRef, closeCalendar]
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeCalendar();
            }
            if (event.key === "Enter") {
                closeCalendar();
            }
        },
        [closeCalendar]
    );

    const setSelectedDate = useCallback(
        (e: Date) => {
            if (onSetSelectedDate && props.onSetSelectedDate) {
                onSetSelectedDate(e);
                props.onSetSelectedDate(e);
                closeCalendar();
            }
        },
        [closeCalendar, onSetSelectedDate, props]
    );

    const handleOutsideActionListeners = useCallback(
        (shouldListen = isCalendarOpen) => {
            let outsideEvents = ["mousedown", "focusin", "touchstart"];
            outsideEvents.forEach(event => {
                if (shouldListen) {
                    document.addEventListener(event, onOutsideAction);
                } else {
                    document.removeEventListener(event, onOutsideAction);
                }
            });

            if (shouldListen) {
                document.addEventListener("keydown", onKeyDown);
            } else {
                document.removeEventListener("keydown", onKeyDown);
            }
        },
        [isCalendarOpen, onOutsideAction, onKeyDown]
    );

    useEffect(() => {
        handleOutsideActionListeners();

        return () => {
            handleOutsideActionListeners(false);
        };
    }, [handleOutsideActionListeners]);

    useEffect(() => {
        if (isCalendarOpen) setCurrentCalendarView(new Date(props.selectedDate ?? new Date()));
    }, [props.selectedDate, isCalendarOpen]);

    const parseTextInput = () => {
        let parsed = parseMomentByCulture(textToParse, props.culture);

        if (textToParse === "") return;

        if (textToParse.trim() === "_") {
            props.onSetSelectedDate(null);
            setInvalidDateInput(false);
        } else if (parsed.isValid && parsed.date) {
            props.onSetSelectedDate(parsed.date);
            setCurrentCalendarView(parsed.date);
            setTextToParse("");
            setInvalidDateInput(false);
        } else {
            setInvalidDateInput(true);
        }
    };

    return (
        <React.Fragment>
            {props.clearable ? (
                <div className="input-group">
                    <input
                        type="text"
                        id={`date-selector-id-${props.name ?? "date-selector"}`}
                        name={props.name ?? "date-selector"}
                        className={`form-control ${props.small === true ? "form-control-sm" : ""} ${invalidDateInput ? "is-invalid" : ""}`}
                        aria-label={props.name ?? "date-selector"}
                        aria-describedby={props.name ?? "date-selector"}
                        autoComplete="off"
                        value={
                            props.selectedDate == null
                                ? "_"
                                : textToParse.length > 0
                                ? textToParse
                                : momentDtoToLocalStringByCulture(props.selectedDate ?? "", props.culture)
                        }
                        onClick={e => {
                            e.stopPropagation();
                            if (!props.readonly) openCalendar(e);
                        }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setTextToParse(e.currentTarget.value);
                        }}
                        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                            if (event.key === "Escape") {
                                parseTextInput();
                            }
                            if (event.key === "Enter") {
                                parseTextInput();
                            }
                        }}
                        onBlur={parseTextInput}
                        ref={inputFieldRef}
                        readOnly={props.readonly === true}
                    />

                    <div
                        className="input-group-append"
                        onClick={e => {
                            e.stopPropagation();
                            if (!props.readonly) props.onSetSelectedDate(null);
                        }}
                    >
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={props.readonly === true}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="red"
                                className="bi bi-x"
                                viewBox="0 0 16 16"
                            >
                                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                            </svg>
                        </button>
                    </div>
                </div>
            ) : (
                <input
                    type="text"
                    id={`date-selector-id-${props.name ?? "date-selector"}`}
                    name={props.name ?? "date-selector"}
                    className={`form-control ${props.small === true ? "form-control-sm" : ""} ${invalidDateInput ? "is-invalid" : ""}`}
                    aria-label={props.name ?? "date-selector"}
                    aria-describedby={props.name ?? "date-selector"}
                    autoComplete="off"
                    value={
                        props.selectedDate == null
                            ? "_"
                            : textToParse.length > 0
                            ? textToParse
                            : momentDtoToLocalStringByCulture(props.selectedDate ?? "", props.culture)
                    }
                    onClick={e => {
                        e.stopPropagation();
                        if (!props.readonly) openCalendar(e);
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setTextToParse(e.currentTarget.value);
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                        if (event.key === "Escape") {
                            parseTextInput();
                        }
                        if (event.key === "Enter") {
                            parseTextInput();
                        }
                    }}
                    onBlur={parseTextInput}
                    ref={inputFieldRef}
                    readOnly={props.readonly === true}
                />
            )}
            {isCalendarOpen ? (
                <DateSelector3Calendar
                    currentCalendarView={currentCalendarView}
                    calendarWrapperRef={calendarWrapperRef}
                    setCurrentCalendarView={setCurrentCalendarView}
                    selectedDate={props.selectedDate ?? new Date()}
                    onSetSelectedDate={e => {
                        setTextToParse("");
                        setInvalidDateInput(false);
                        setSelectedDate(e);
                    }}
                    culture={props.culture}
                    calendarPositionLeft={calendarPositionLeft}
                    small={props.small}
                    marginTop={props.marginTop}
                    clearable={props.clearable}
                ></DateSelector3Calendar>
            ) : null}
        </React.Fragment>
    );
};

export default DateSelector3;
